import {
  type CheckboxFilterDescriptor,
  type CustomFilterDescriptor,
  FilterKind,
  type FilterOptionLabel,
  type RadioFilterDescriptor,
  type SearchFilterDescriptor,
  type TableFilterDescriptorOption,
} from '@fly/shared';

/** Config for creating a radio (single-select) filter. */
export type CreateRadioFilterConfig<T = unknown> = {
  key: string;
  label: FilterOptionLabel;
  hideFromAddDropdown?: boolean;
} & (
  | {
      options: TableFilterDescriptorOption[];
      clientFilterFn?: (row: T, value: string) => boolean;
    }
  | {
      getOptions: () =>
        | TableFilterDescriptorOption[]
        | Promise<TableFilterDescriptorOption[]>;
      filterFn?: (row: T, value: string) => boolean;
    }
);

/** Config for creating a checkbox (multi-select) filter. */
export type CreateCheckboxFilterConfig<T = unknown> = {
  key: string;
  label: FilterOptionLabel;
  hideFromAddDropdown?: boolean;
} & (
  | {
      options: TableFilterDescriptorOption[];
      clientFilterFn?: (row: T, value: string) => boolean;
    }
  | {
      getOptions: () =>
        | TableFilterDescriptorOption[]
        | Promise<TableFilterDescriptorOption[]>;
      filterFn?: (row: T, value: string) => boolean;
    }
);

/** Config for creating the search filter descriptor. */
export type CreateSearchFilterConfig = {
  key?: string;
  label: FilterOptionLabel;
  hideFromAddDropdown?: boolean;
};

const DEFAULT_SEARCH_KEY = 'search';

/**
 * Creates a radio (single-value) filter descriptor.
 * Use with TanstackTable filter system; options can be static or from getOptions (dynamic).
 */
export function createRadioFilter<T = unknown>(
  config: CreateRadioFilterConfig<T>
): RadioFilterDescriptor<T> {
  const optionsSource =
    'options' in config
      ? {
          type: 'static' as const,
          options: config.options,
          clientFilterFn: config.clientFilterFn,
        }
      : {
          type: 'dynamic' as const,
          getOptions: config.getOptions,
          filterFn: config.filterFn,
        };
  return {
    kind: FilterKind.RADIO,
    key: config.key,
    label: config.label,
    hideFromAddDropdown: config.hideFromAddDropdown,
    optionsSource,
  };
}

/**
 * Creates a checkbox (multi-value) filter descriptor.
 * URL value is comma-separated (e.g. f.severity=high,medium,low).
 */
export function createCheckboxFilter<T = unknown>(
  config: CreateCheckboxFilterConfig<T>
): CheckboxFilterDescriptor<T> {
  const optionsSource =
    'options' in config
      ? {
          type: 'static' as const,
          options: config.options,
          clientFilterFn: config.clientFilterFn,
        }
      : {
          type: 'dynamic' as const,
          getOptions: config.getOptions,
          filterFn: config.filterFn,
        };
  return {
    kind: FilterKind.CHECKBOX,
    key: config.key,
    label: config.label,
    hideFromAddDropdown: config.hideFromAddDropdown,
    optionsSource,
  };
}

/**
 * Creates the free-search filter descriptor.
 * There is typically one search filter per table; key defaults to "search".
 */
export function createSearchFilter(
  config: CreateSearchFilterConfig
): SearchFilterDescriptor {
  return {
    kind: FilterKind.SEARCH,
    key: config.key ?? DEFAULT_SEARCH_KEY,
    label: config.label,
    hideFromAddDropdown: config.hideFromAddDropdown ?? true,
  };
}

/** Config for creating a custom filter (e.g. prompt filter with custom UI). */
export type CreateCustomFilterConfig<T = unknown> = {
  key: string;
  label: FilterOptionLabel;
  hideFromAddDropdown?: boolean;
  FilterView: CustomFilterDescriptor<T>['FilterView'];
  filterFn?: (row: T, value: string) => boolean;
};

/**
 * Creates a custom filter descriptor; use for filters with custom UI (e.g. FlyFilterByPrompt).
 * Same state/URL as other filters.
 */
export function createCustomFilter<T = unknown>(
  config: CreateCustomFilterConfig<T>
): CustomFilterDescriptor<T> {
  return {
    kind: FilterKind.CUSTOM,
    key: config.key,
    label: config.label,
    hideFromAddDropdown: config.hideFromAddDropdown,
    FilterView: config.FilterView,
    filterFn: config.filterFn,
  };
}
